import {css, StyleSheet} from 'aphrodite';
import React, {DragEventHandler, useState} from 'react';

import {FlowchartDragDropKey} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {WHITE} from 'ui-library/Colors';
import {ElementDragEvent} from 'util/Types';

/**
 * Provides tools for rendering a sidebar with draggable nodes for GraphBuilder.
 *
 * Note that the GraphBuilderSidebar component doesn't impose any restrictions
 * on the contents of your sidebar. However, you should use its members (e.g.,
 * GraphBuilderSidebar.Spacer, GraphBuilderSidebar.DraggableNode) to make use of
 * the provided features and styling.
 */

export const enum GraphBuilderSidebarSide {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

interface GraphBuilderSidebarProps {
  side: GraphBuilderSidebarSide;
  children: React.ReactNode;
}

interface DraggableNodeRenderParams {
  isHighlighted: boolean;
  onDragStart: DragEventHandler;
  onDragEnd: DragEventHandler;
}

interface GraphBuilderDraggableNodeProps {
  dragKey: string;
  renderNode: (params: DraggableNodeRenderParams) => JSX.Element;
}

// Exposed as GraphBuilderSidebar.DraggableNode
function GraphBuilderDraggableNode(props: GraphBuilderDraggableNodeProps): JSX.Element {
  const [isDragging, setDragging] = useState(false);

  function dragStartHandler(event: ElementDragEvent): void {
    event.dataTransfer.setData(FlowchartDragDropKey, props.dragKey);
    event.dataTransfer.effectAllowed = 'move';
    setDragging(true);
  }

  function dragEndHandler(): void {
    setDragging(false);
  }

  return (
    <div
      className={css(isDragging ? styles.draggingNode : styles.draggableNode)}
      {...getTestProps({component: 'GraphBuilderDraggableNode', element: 'taskNode', key: props.dragKey})}
      data-amp-safe={false}
    >
      <div className={css(styles.draggableNodeContent)}>
        {props.renderNode({
          isHighlighted: isDragging,
          onDragStart: dragStartHandler,
          onDragEnd: dragEndHandler,
        })}
      </div>
    </div>
  );
}

// Exposed as GraphBuilderSidebar.Spacer
function Spacer(): JSX.Element {
  return <div className={css(styles.spacer)} />;
}

// Exposed as GraphBuilderSidebar.Heading
function Heading(props: {text: string}): JSX.Element {
  return <h3 className={css(styles.heading)}>{props.text}</h3>;
}

// Exposed as GraphBuilderSidebar.Subheading
function Subheading(props: {text: string}): JSX.Element {
  return <p className={css(styles.subheading)}>{props.text}</p>;
}

// Exposed as GraphBuilderSidebar.Divider
function Divider(): JSX.Element {
  return <hr className={css(styles.divider)} />;
}

/**
 * Renders a sidebar for template nodes. Each node can be dragged onto the GraphBuilder canvas.
 */
export function GraphBuilderSidebar(props: GraphBuilderSidebarProps): JSX.Element {
  return (
    <aside
      className={css(
        styles.aside,
        props.side === GraphBuilderSidebarSide.LEFT ? styles.asideLeft : styles.asideRight
      )}
    >
      {props.children}
    </aside>
  );
}

// Note: attaching these properties is fine, but will break if you attach them to the default export.
// This is why GBSidebar is a named export!
GraphBuilderSidebar.DraggableNode = GraphBuilderDraggableNode;
GraphBuilderSidebar.Spacer = Spacer;
GraphBuilderSidebar.Heading = Heading;
GraphBuilderSidebar.Divider = Divider;
GraphBuilderSidebar.Subheading = Subheading;

const styles = StyleSheet.create({
  aside: {
    position: 'absolute',
    zIndex: 100,
    height: 'calc(100% - 32px)',
    width: '195px',
    background: WHITE,
    overflow: 'hidden',
  },
  asideRight: {
    right: 0,
  },
  asideLeft: {
    left: 0,
  },
  draggableNode: {margin: '8px', cursor: 'grab'},
  draggingNode: {margin: '8px', cursor: 'grabbing'},
  draggableNodeContent: {width: '100%', height: '100%'},
  spacer: {height: '4px'},
  heading: {fontSize: '13px', lineHeight: '16px', marginLeft: '10px'},
  subheading: {fontSize: '13px', lineHeight: '16px'},
  divider: {marginTop: '1px'},
});
